import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ClaimInput, Tenant, TenantStatus } from '../../identity/api';
import { claimKey, sameClaim, withClaims } from '../../identity/claimCatalog';
import { Button, Chip, Dialog, Switch } from '@nyce/admin-kit/ui';
import { client, useLogin, tenantPath } from '../../identity/session';
import { fmtDate, sortedByName } from '../../identity/format';
import {
  Code,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  Pager,
  TableScroller,
  useAsync,
  useNotice,
} from '../../components/common';
import { TenantFields, toCreate, useTenantForm } from './tenant/TenantForm';
import { CopyTenantDialog } from './tenant/CopyTenantDialog';
import { ClaimPicker } from './tenant/ClaimPicker';

const PAGE_SIZE = 25;

export function StatusChip({ status }: { status: TenantStatus }) {
  return (
    <Chip size="sm" selected={status === 'Active'} className={`oph-status-${status.toLowerCase()}`}>
      {status}
    </Chip>
  );
}

function NewTenantDialog({
  onCreated,
  onClose,
  notify,
}: {
  onCreated: (t: Tenant) => void;
  onClose: () => void;
  notify: { error: (t: string) => void };
}) {
  const form = useTenantForm({
    name: '',
    identityProvider: 'Workserver',
    identityProviderUrl: '',
    customerId: '',
  });
  const [claims, setClaims] = useState<ClaimInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const t = await client.tenants.create(toCreate(form.value));
      // AUTH's create takes no claims: a bare-array replace right after (as "New as copy" does).
      if (claims.length) {
        try {
          await client.tenants.claims.replace(t.id, claims);
        } catch (e) {
          notify.error(`Created ${t.name}, but its claims were not saved: ${(e as Error).message}`);
        }
      }
      onCreated(t);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="New tenant"
      maxWidth={720}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void save()} loading={busy} disabled={!form.valid}>
            Create
          </Button>
        </>
      }
    >
      <ErrorBox error={error} />
      <TenantFields value={form.value} onChange={form.setValue} />
      <h3 className="nyk-dialog-section">Claims</h3>
      <div className="nyk-tags-box" data-testid="new-tenant-claims">
        {claims.map((c) => (
          <span key={claimKey(c)} className="nyk-ui-chip nyk-ui-chip-sm nyk-tag">
            {c.type === 'scope' ? c.value : `${c.type}: ${c.value}`}
            <button
              type="button"
              className="nyk-tag-remove"
              aria-label={`remove ${c.type} ${c.value}`}
              onClick={() => setClaims(claims.filter((x) => !sameClaim(x, c)))}
            >
              ×
            </button>
          </span>
        ))}
        {!claims.length && (
          <span className="nyk-ui-hint">None yet: tokens get an empty scope (default deny).</span>
        )}
      </div>
      <ClaimPicker
        value={claims}
        onAdd={(cs) => setClaims(withClaims(claims, cs))}
        onRemove={(c) => setClaims(claims.filter((x) => !sameClaim(x, c)))}
      />
      <p className="nyk-card-intro" style={{ marginTop: 14 }}>
        Roles, service keys, clients and local users are managed on the tenant page after it is created.
      </p>
    </Dialog>
  );
}

/** Tenants: the unit AUTH issues tokens for. Each references a customer and picks its identity provider. */
export function TenantsPage() {
  const login = useLogin();
  const canWrite = !!login?.access.canWrite;
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const tenants = useAsync(() => client.tenants.list({ page, pageSize: PAGE_SIZE }), [page]);
  const [creating, setCreating] = useState(false);
  const [copying, setCopying] = useState<Tenant | null>(null);
  // DELETE is a soft delete and AUTH's list has no status filter, so deleted tenants are hidden here.
  const [showDeleted, setShowDeleted] = useState(false);
  const items = tenants.data?.items ?? [];
  const hiddenCount = showDeleted ? 0 : items.filter((t) => t.status === 'Deleted').length;
  const rows = sortedByName(showDeleted ? items : items.filter((t) => t.status !== 'Deleted'), (t) => t.name);
  const toast = useNotice();
  const remove = async (t: Tenant) => {
    try {
      await client.tenants.remove(t.id);
      await tenants.reload();
      toast.success(`Deleted ${t.name} (status Deleted; sign-in refused)`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <Page
      title="Tenants"
      description="Every token is issued for one tenant: its claims become the scope and roles, its customer the orgid."
      actions={
        <>
          <Switch checked={showDeleted} onChange={setShowDeleted} label="Show deleted" />
          {canWrite && <Button onClick={() => setCreating(true)}>New tenant</Button>}
        </>
      }
    >
      <ErrorBox error={tenants.error} />
      {toast.element}
      <Loading loading={tenants.loading} />
      <div className="nyk-card">
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="tenants-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Customer</th>
                <th>Identity provider</th>
                <th>Status</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  data-tenant={t.id}
                  className={t.status === 'Deleted' ? 'nyk-row-muted' : undefined}
                >
                  <td className="nyk-card-title nyk-wrap">
                    <Link to={tenantPath(t.id)} className="nyk-link">
                      {t.name}
                    </Link>
                    <div className="nyk-ui-hint">
                      <Code>{t.id}</Code>
                    </div>
                  </td>
                  <td data-label="Customer" className="nyk-wrap">
                    {t.customerName ?? <Code>{t.customerId}</Code>}
                  </td>
                  <td data-label="Identity provider">
                    {t.identityProvider}
                    {t.identityProvider === 'Workserver' && (
                      <div className="nyk-ui-hint nyk-url">{t.identityProviderUrl}</div>
                    )}
                  </td>
                  <td data-label="Status">
                    <StatusChip status={t.status} />
                  </td>
                  <td data-label="Updated">{fmtDate(t.updatedAt)}</td>
                  <td className="nyk-row-actions">
                    <Button size="sm" variant="ghost" onClick={() => navigate(tenantPath(t.id))}>
                      Open
                    </Button>
                    {canWrite && (
                      <Button size="sm" variant="ghost" onClick={() => setCopying(t)}>
                        Copy
                      </Button>
                    )}
                    {canWrite && t.status !== 'Deleted' && (
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Delete tenant ${t.name}? Sign-in is refused afterwards; claims, keys and users stay in the database.`}
                        onConfirm={() => void remove(t)}
                      >
                        Delete
                      </ConfirmButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!tenants.loading && !items.length && (
          <EmptyState title="No tenants yet." hint="Create a customer, then a tenant that references it." />
        )}
        {!tenants.loading && !!items.length && !rows.length && (
          <EmptyState title="Only deleted tenants on this page." hint="Turn on “Show deleted” to see them." />
        )}
        {hiddenCount > 0 && !!rows.length && (
          <p className="nyk-ui-hint" data-testid="hidden-deleted">
            {hiddenCount} deleted {hiddenCount === 1 ? 'tenant' : 'tenants'} hidden on this page.
          </p>
        )}
        {tenants.data && (
          <Pager
            page={tenants.data.page}
            pageSize={tenants.data.pageSize}
            total={tenants.data.total}
            onPage={setPage}
          />
        )}
      </div>
      {creating && (
        <NewTenantDialog
          onCreated={(t) => {
            toast.success(`Created ${t.name}`);
            navigate(tenantPath(t.id));
          }}
          onClose={() => setCreating(false)}
          notify={toast}
        />
      )}
      {copying && (
        <CopyTenantDialog
          source={copying}
          onCreated={(t, warning) => {
            if (warning) toast.error(warning);
            else toast.success(`Created ${t.name} as a copy of ${copying.name}`);
            navigate(tenantPath(t.id));
          }}
          onClose={() => setCopying(null)}
        />
      )}
    </Page>
  );
}
